import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CircularProgress from '@mui/material/CircularProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LgmButton from '../../LgmButton';
import { GRAY_100, GRAY_400, GRAY_500, GRAY_900 } from 'theme/grayScale';
import { COLOR_DANGER_TEXT, COLOR_INFO_TEXT } from 'theme/colors';
import { formatDateForDisplay } from '../../../utils/dateConversion';
import { formatDmfStatus, formatDmfType } from '../../../utils/dmfTypeLabels';
import { parseNdcActiveIngredients } from '../../../utils/ndcActiveIngredients';
import TeCodeHeaderTooltip from '../../TeCodeHeaderTooltip';
import TeCodeValue from '../../TeCodeValue';

export interface LinkedProductPreview {
  productId: number;
  productName: string;
  /** NDC only (MFGV3-003, Sub-task 3.1) — disambiguates rows sharing a `productName` but at different real strengths. Also OB's aggregated per-ingredient strength (MFGV3-006 UI follow-up) — the two sources never share a record, so one field covers both. */
  strength?: string | null;
  /** Orange Book only (MFGV3-004, Sub-sub-task 4.3.2) — the group's reconstructed combined-ingredient string, for the Seq 6 detail view. */
  aggregatedIngredients?: string | null;
  /** USDMF only (MFGV3-005, Sub-task 5.2) — always shown, not just as a null-subject fallback, so distinct multi-filing cases stay distinguishable after byte-identical dedup. */
  dmfNumber?: number | null;
  submitDate?: string | null;
  /** Detail-view-only fields (MFGV3-006, Sub-sub-task 6.1.1). NDC: productNdc/productType. Orange Book: applicant/route/teCode. USDMF: holder/status/dmfType. */
  productNdc?: string | null;
  productType?: string | null;
  applicant?: string | null;
  route?: string | null;
  teCode?: string | null;
  holder?: string | null;
  status?: string | null;
  dmfType?: string | null;
  /**
   * Detail-view field-completeness pass (post-manual-testing feedback,
   * 2026-09-10) — mirrors the Product modal's own NDCPanel/OrangeBookPanel
   * detail fields, this feature's established "what to show" pattern.
   */
  labelerName?: string | null;
  deaSchedule?: string | null;
  marketingStartDate?: string | null;
  marketingEndDate?: string | null;
  finished?: boolean | null;
  activeIngredients?: string | null;
  applType?: string | null;
  applNo?: string | null;
  dosageForm?: string | null;
  approvalDate?: string | null;
}

/** A single read-only label/value row inside the detail section. */
const DetailFieldRow: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div style={{ display: 'flex', gap: '8px', padding: '2px 0' }}>
    <span
      style={{
        minWidth: '110px',
        fontWeight: 600,
        color: GRAY_500,
        flexShrink: 0,
        fontSize: '0.75rem',
      }}>
      {label}
    </span>
    <span style={{ color: GRAY_900, fontSize: '0.75rem' }}>{value}</span>
  </div>
);

/** A label/value row whose value is itself a short list (NDC active ingredients). */
const DetailFieldListRow: React.FC<{ label: string; items: string[] }> = ({
  label,
  items,
}) => (
  <div style={{ display: 'flex', gap: '8px', padding: '2px 0' }}>
    <span
      style={{
        minWidth: '110px',
        fontWeight: 600,
        color: GRAY_500,
        flexShrink: 0,
        fontSize: '0.75rem',
      }}>
      {label}
    </span>
    <div style={{ color: GRAY_900, fontSize: '0.75rem' }}>
      {items.length === 0
        ? '—'
        : items.map((item, index) => (
            <div key={`${item}-${index}`}>{item}</div>
          ))}
    </div>
  </div>
);

function formatNdcFinished(finished: boolean | null | undefined): string {
  if (finished === null || finished === undefined) return '—';
  return finished ? 'Finished' : 'Unfinished';
}

/**
 * Full source-record detail for one selected linked product (MFGV3-006,
 * Sub-sub-task 6.1.2/6.1.3) — feature-detects which source's fields are
 * populated (each source's response only carries its own fields, per
 * `manufacturerLinkedProducts.js`), since this panel isn't told the
 * source's key, only its display label. Field sets mirror the Product
 * modal's own NDCPanel/OrangeBookPanel/DMFPanel detail views (this
 * feature's established "what to show, how to format it" pattern) —
 * including repeating the product's own name/subject/trade name as an
 * explicit field, not just relying on the radio label above it.
 */
const LinkedProductDetail: React.FC<{ product: LinkedProductPreview }> = ({
  product,
}) => {
  const rows: Array<
    | { label: React.ReactNode; value: React.ReactNode }
    | { label: string; items: string[] }
  > = [];

  if (product.productNdc !== undefined) {
    // NDC — mirrors NDCPanel.tsx's field set/order.
    rows.push({ label: 'NDC', value: product.productNdc ?? '—' });
    rows.push({ label: 'Generic Name', value: product.productName });
    rows.push({
      label: 'Active Ingredients',
      items: parseNdcActiveIngredients(product.activeIngredients).map(entry =>
        entry.strength ? `${entry.name} — ${entry.strength}` : entry.name
      ),
    });
    rows.push({ label: 'Labeler', value: product.labelerName ?? '—' });
    rows.push({ label: 'Product Type', value: product.productType ?? '—' });
    rows.push({ label: 'Status', value: formatNdcFinished(product.finished) });
    rows.push({ label: 'DEA Schedule', value: product.deaSchedule ?? '—' });
    rows.push({
      label: 'Marketing Start',
      value: formatDateForDisplay(product.marketingStartDate) || '—',
    });
    rows.push({
      label: 'Marketing End',
      value: formatDateForDisplay(product.marketingEndDate) || '—',
    });
  } else if (product.aggregatedIngredients !== undefined) {
    // Orange Book — mirrors OrangeBookPanel.tsx's field set/order.
    const application =
      `${product.applType ?? ''}${product.applNo ?? ''}`.trim();
    rows.push({ label: 'Trade Name', value: product.productName ?? '—' });
    rows.push({
      label: 'Ingredients',
      value: product.aggregatedIngredients ?? '—',
    });
    rows.push({ label: 'Application', value: application || '—' });
    rows.push({
      label: 'Approval Date',
      value: formatDateForDisplay(product.approvalDate) || '—',
    });
    rows.push({
      label: (
        <>
          TE Code
          <TeCodeHeaderTooltip />
        </>
      ),
      value: <TeCodeValue code={product.teCode ?? null} />,
    });
    rows.push({ label: 'Strength', value: product.strength ?? '—' });
    rows.push({ label: 'Dosage Form', value: product.dosageForm ?? '—' });
    rows.push({ label: 'Route', value: product.route ?? '—' });
    rows.push({ label: 'Applicant', value: product.applicant ?? '—' });
  } else if (product.dmfNumber !== undefined) {
    // USDMF — mirrors DMFPanel.tsx's field set/order, including its
    // 'A'/'I' -> 'Active'/'Inactive' and dmf_type -> 'API'/'Excipient'
    // humanization (dmfTypeLabels.ts), rather than showing raw codes.
    rows.push({
      label: 'DMF Number',
      value: product.dmfNumber != null ? String(product.dmfNumber) : '—',
    });
    rows.push({ label: 'Type', value: formatDmfType(product.dmfType ?? null) });
    rows.push({
      label: 'Status',
      value: formatDmfStatus(product.status ?? null),
    });
    rows.push({ label: 'Subject', value: product.productName ?? '—' });
    rows.push({ label: 'Holder', value: product.holder ?? '—' });
    rows.push({
      label: 'Submit Date',
      value: formatDateForDisplay(product.submitDate) || '—',
    });
  }

  return (
    <div
      style={{
        padding: '6px 10px',
        backgroundColor: GRAY_100,
        borderRadius: '4px',
        // Static — does not scroll with the product list (the list column
        // scrolls internally; this pane's own height just follows its content).
      }}>
      {rows.map((row, index) =>
        'items' in row ? (
          <DetailFieldListRow
            key={`${row.label}-${index}`}
            label={row.label}
            items={row.items}
          />
        ) : (
          <DetailFieldRow key={index} label={row.label} value={row.value} />
        )
      )}
    </div>
  );
};

export interface LinkedProductsPanelProps {
  sourceLabel: string;
  /** Distinguishes this panel's native radio group from another panel for
   * the same source (e.g. two DECRS registrations, two NDC labelers) —
   * without it, every instance shares `linked-products-${sourceLabel}` and
   * the browser treats their radios as one group, so selecting one
   * visually unchecks another instance's own selection (Copilot round 10). */
  groupKey: string;
  /** Total linked-product count for this manufacturer/source pair. */
  totalCount: number;
  /** The current page's items — up to `pageSize` long. */
  products: LinkedProductPreview[];
  /** 0-indexed current page. */
  page: number;
  pageSize: number;
  status: 'loading' | 'error' | 'success';
  /** True while a same-list refetch (e.g. a page change) is in flight but `products`/`totalCount` still reflect the previous page (MFGV3-002, Sub-task 2.1) — renders a subtle in-progress indicator instead of replacing the list with a bare loading state. */
  isRefetching?: boolean;
  onPageChange: (page: number) => void;
}

/**
 * Shared read-only linked-products display for manufacturer source panels
 * (MFGV2-003, Sub-task 3.3) — total count, a paginated preview (10 per
 * page), and empty/loading/error states. Per Open Question 4's resolution:
 * read-only display only, no product-level FK linkage.
 *
 * Purely presentational (data comes in via props, not fetched internally):
 * no manufacturer source has a real product-junction table yet (NDC/Orange
 * Book/USDMF manufacturer-level linking ships in Seq 4-6) — building a
 * self-fetching component against a data source that doesn't exist yet
 * would be speculative. Seq 4-6 wires a real data-fetching hook to this
 * same component once each source's junction table and matcher exist; the
 * UI itself needs no further work at that point.
 *
 * The collapsible toggle mirrors CASRegistryPanel's "More details" section
 * (issue #654 Seq 16 QA follow-up) — a real MUI `Accordion` +
 * `ExpandMoreIcon`, not a hand-rolled unicode arrow glyph, which read as an
 * illegibly tiny dot at this font size.
 *
 * Layout (post-manual-testing feedback, 2026-09-10): selecting a radio
 * splits this panel into two side-by-side columns instead of inserting the
 * detail block between list rows — list on the left (scrollable, fixed
 * height), detail on the right (static, grows/shrinks with its own
 * content, never scrolls). With nothing selected the list uses the full
 * width. Re-clicking the currently-selected radio deselects it (native
 * HTML radios can't be unchecked by clicking them again — `onClick`, not
 * `onChange`, is the only reliable place to detect that specific case,
 * since a click on an already-checked radio never fires a change event).
 */
const LinkedProductsPanel: React.FC<LinkedProductsPanelProps> = ({
  sourceLabel,
  groupKey,
  totalCount,
  products,
  page,
  pageSize,
  status,
  isRefetching = false,
  onPageChange,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null
  );

  // Changing page deselects the radio and clears the detail section
  // (MFGV3-006, Sub-task 6.2) — the selected product no longer belongs to
  // the page now being shown.
  useEffect(() => {
    setSelectedProductId(null);
  }, [page]);

  if (status === 'loading') {
    return (
      <Typography
        variant="body2"
        style={{ color: GRAY_500, fontSize: '0.8125rem' }}>
        Loading {sourceLabel} products...
      </Typography>
    );
  }

  if (status === 'error') {
    return (
      <Typography
        variant="body2"
        style={{ color: COLOR_DANGER_TEXT, fontSize: '0.8125rem' }}>
        Failed to load {sourceLabel} products.
      </Typography>
    );
  }

  if (totalCount === 0) {
    return (
      <Typography
        variant="body2"
        style={{ color: GRAY_400, fontStyle: 'italic', fontSize: '0.8125rem' }}>
        No {sourceLabel} products yet
      </Typography>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const selectedProduct =
    products.find(p => p.productId === selectedProductId) ?? null;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(prev => !prev)}
      slotProps={{ transition: { unmountOnExit: false } }}
      disableGutters
      elevation={0}
      sx={{ '&:before': { display: 'none' } }}
      style={{ backgroundColor: 'transparent' }}>
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            style={{ color: COLOR_INFO_TEXT, fontSize: '18px' }}
          />
        }
        style={{
          minHeight: '32px',
          padding: 0,
          fontSize: '0.75rem',
          color: GRAY_500,
          backgroundColor: 'transparent',
        }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {totalCount} {sourceLabel} product{totalCount === 1 ? '' : 's'}
          {isRefetching && (
            <CircularProgress
              size={11}
              thickness={5}
              style={{ color: GRAY_500 }}
              aria-label={`Refreshing ${sourceLabel} products`}
            />
          )}
        </span>
      </AccordionSummary>
      <AccordionDetails
        style={{
          padding: '0 0 4px 0',
          opacity: isRefetching ? 0.6 : 1,
          transition: 'opacity 150ms ease-in-out',
        }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <div
            style={{
              flex: selectedProduct ? '1 1 50%' : '1 1 100%',
              minWidth: 0,
              maxHeight: '260px',
              overflowY: 'auto',
            }}>
            <RadioGroup
              aria-label={`${sourceLabel} linked products`}
              name={`linked-products-${sourceLabel}-${groupKey}`}
              value={
                selectedProductId === null ? '' : String(selectedProductId)
              }
              onChange={event =>
                setSelectedProductId(Number(event.target.value))
              }>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {products.map(product => (
                  <li key={product.productId}>
                    <FormControlLabel
                      value={String(product.productId)}
                      style={{ marginLeft: 0 }}
                      control={
                        <Radio
                          size="small"
                          style={{ padding: '2px' }}
                          onClick={() => {
                            if (selectedProductId === product.productId) {
                              setSelectedProductId(null);
                            }
                          }}
                        />
                      }
                      label={
                        <span
                          style={{ fontSize: '0.8125rem', color: GRAY_900 }}>
                          {product.productName}
                          {product.aggregatedIngredients === undefined &&
                            product.strength &&
                            ` — ${product.strength}`}
                          {product.dmfNumber != null &&
                            ` — DMF #${product.dmfNumber}`}
                        </span>
                      }
                    />
                  </li>
                ))}
              </ul>
            </RadioGroup>
            {totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '6px',
                }}>
                <LgmButton
                  variant="flat"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 0}
                  style={{ fontSize: '11px', padding: '2px 8px' }}>
                  Previous
                </LgmButton>
                <Typography
                  variant="body2"
                  style={{ fontSize: '0.75rem', color: GRAY_500 }}>
                  Page {page + 1} of {totalPages}
                </Typography>
                <LgmButton
                  variant="flat"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages - 1}
                  style={{ fontSize: '11px', padding: '2px 8px' }}>
                  Next
                </LgmButton>
              </div>
            )}
          </div>
          {selectedProduct && (
            <div style={{ flex: '1 1 50%', minWidth: 0 }}>
              <LinkedProductDetail product={selectedProduct} />
            </div>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default LinkedProductsPanel;
